import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Download, Printer, RefreshCw, Search, Trash2, X } from 'lucide-react'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import IsolatedLexicalEditor from '../components/IsolatedLexicalEditor'
import Button from '../components/Button'
import Input from '../components/Input'

function formatDateTime(value) {
  if (!value) return '-'

  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function updateBadge(plugin) {
  if (plugin.update_available) {
    return 'bg-amber-100 text-amber-800'
  }

  return 'bg-emerald-100 text-emerald-700'
}

function defaultExportFromDate() {
  const date = new Date()
  const month = String(date.getMonth() + 1).padStart(2, '0')

  return `${date.getFullYear()}-${month}-01`
}

const MIGRATION_COMPONENTS = ['database', 'media', 'plugins', 'themes']

const ENVIRONMENT_OPTIONS = [
  ['production', 'Production'],
  ['staging', 'Staging'],
  ['local', 'Local'],
  ['dev', 'Dev'],
]

function filenameFromDisposition(value) {
  if (!value) return ''

  const match = value.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
  const filename = match?.[1] || match?.[2] || ''

  return filename ? decodeURIComponent(filename) : ''
}

async function exportErrorMessage(error) {
  const data = error?.response?.data

  if (data && typeof data.text === 'function') {
    const text = await data.text()

    try {
      const parsed = JSON.parse(text)
      return parsed?.message || text || 'Version changes export failed.'
    } catch {
      return text || 'Version changes export failed.'
    }
  }

  return error?.response?.data?.message || error?.message || 'Version changes export failed.'
}

function textNode(text) {
  return {
    detail: 0,
    format: 0,
    mode: 'normal',
    style: '',
    text: String(text ?? ''),
    type: 'text',
    version: 1,
  }
}

function paragraphNode(text = '') {
  return {
    children: text ? [textNode(text)] : [],
    direction: 'ltr',
    format: '',
    indent: 0,
    type: 'paragraph',
    version: 1,
  }
}

function headingNode(tag, text) {
  return {
    children: [textNode(text)],
    direction: 'ltr',
    format: '',
    indent: 0,
    tag,
    type: 'heading',
    version: 1,
  }
}

function lexicalDocument(children) {
  return {
    root: {
      children: children.length ? children : [paragraphNode()],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  }
}

function siteLabel(report) {
  const site = report.site || {}

  return site.name || site.home_url || site.site_url || site.site_uuid || 'Maintenance Site'
}

function formatVersion(value) {
  return value || '-'
}

function pluginVersionRows(report) {
  if (Array.isArray(report.plugin_version_changes)) {
    return report.plugin_version_changes
  }

  const plugins = Array.isArray(report.plugin_versions) ? report.plugin_versions : []
  const changes = Array.isArray(report.update_log)
    ? report.update_log.filter((item) => item.item_type === 'plugin')
    : []
  const versionChangesById = new Map()
  const rowsById = new Map()

  changes.forEach((change) => {
    if (!change.identifier) return

    if (['version_changed', 'installed', 'removed'].includes(change.change)) {
      versionChangesById.set(change.identifier, change)
    }
  })

  plugins.forEach((plugin) => {
    const identifier = plugin.file || plugin.identifier || plugin.slug || plugin.name
    const versionChange = versionChangesById.get(identifier)

    rowsById.set(identifier || plugin.name, {
      item_name: plugin.name || plugin.file || 'Plugin',
      identifier,
      old_version: versionChange?.from_version || plugin.version || '',
      new_version: versionChange?.to_version || plugin.version || '',
      latest_version: plugin.latest_version || plugin.version || '',
      change: versionChange?.change || 'unchanged',
      update_available: Boolean(plugin.update_available),
    })
  })

  changes.forEach((change) => {
    const identifier = change.identifier || change.item_name
    if (!identifier || rowsById.has(identifier)) return

    rowsById.set(identifier, {
      item_name: change.item_name || identifier,
      identifier,
      old_version: change.from_version || '',
      new_version: change.to_version || '',
      latest_version: change.to_version || '',
      change: change.change || 'version_changed',
      update_available: false,
    })
  })

  return Array.from(rowsById.values()).sort((a, b) => {
    return (a.item_name || '').localeCompare(b.item_name || '', undefined, { sensitivity: 'base' })
  })
}

function pluginVersionLine(row) {
  const latestVersion = row.latest_version && row.latest_version !== row.new_version
    ? `, latest available ${row.latest_version}`
    : ''
  const status = row.update_available ? ', update available' : ''
  const change = row.change && row.change !== 'unchanged' ? ` (${changeLabel(row.change)})` : ''

  return `${row.item_name || row.name || row.identifier || 'Plugin'}: ${formatVersion(row.old_version)} --> ${formatVersion(row.new_version)}${latestVersion}${status}${change}`
}

function buildPdfCreatorDocument(report) {
  const wordpress = report.wordpress || {}
  const activeTheme = report.active_theme || {}
  const environment = report.environment || {}
  const updatesAvailable = Array.isArray(report.updates_available) ? report.updates_available : []
  const updateLog = Array.isArray(report.update_log) ? report.update_log : []
  const pluginRows = pluginVersionRows(report)
  const children = [
    headingNode('h1', 'Maintenance Activity Report'),
    paragraphNode(`Site: ${siteLabel(report)}`),
    paragraphNode(`Period: ${report.from || '-'} to ${report.to || '-'}`),
    paragraphNode(`Report date: ${formatDateTime(report.current_report?.reported_at)}`),
    paragraphNode(),
    headingNode('h2', 'Summary'),
    paragraphNode(`WordPress reported version: ${formatVersion(wordpress.version)}`),
    paragraphNode(`Latest WordPress version: ${formatVersion(wordpress.latest_version || wordpress.version)}`),
    paragraphNode(`Updates available: ${updatesAvailable.length}`),
    paragraphNode(`Plugins tracked: ${pluginRows.length}`),
    paragraphNode(),
    headingNode('h2', 'Plugin Versions'),
    paragraphNode('Format: old version --> new version'),
  ]

  if (pluginRows.length === 0) {
    children.push(paragraphNode('No plugin version data is available for this report.'))
  } else {
    pluginRows.forEach((row) => {
      children.push(paragraphNode(pluginVersionLine(row)))
    })
  }

  children.push(paragraphNode(), headingNode('h2', 'Updates Available'))

  if (updatesAvailable.length === 0) {
    children.push(paragraphNode('No updates available in current snapshot.'))
  } else {
    updatesAvailable.forEach((item) => {
      children.push(paragraphNode(`${item.item_name}: ${formatVersion(item.current_version)} --> ${formatVersion(item.latest_version)}`))
    })
  }

  children.push(paragraphNode(), headingNode('h2', 'Update Log'))

  if (updateLog.length === 0) {
    children.push(paragraphNode('No version changes detected for selected period.'))
  } else {
    updateLog.forEach((item) => {
      children.push(paragraphNode(`${item.item_name}: ${changeLabel(item.change)} (${formatVersion(item.from_version)} --> ${formatVersion(item.to_version)})`))
    })
  }

  children.push(
    paragraphNode(),
    headingNode('h2', 'Site Inventory'),
    paragraphNode(`Active theme: ${activeTheme.name || '-'} ${activeTheme.version || ''}`.trim()),
    paragraphNode(`Theme stylesheet: ${activeTheme.stylesheet || '-'}`),
    paragraphNode(`PHP: ${environment.php_version || '-'}`),
    paragraphNode(`MySQL: ${environment.mysql_version || '-'}`),
    paragraphNode(`Timezone: ${environment.timezone || '-'}`),
  )

  return lexicalDocument(children)
}

export default function MaintenanceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isSuperAdmin } = useAuthStore()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [updatesOnly, setUpdatesOnly] = useState(false)
  const [exportFrom, setExportFrom] = useState(defaultExportFromDate)
  const [isExporting, setIsExporting] = useState(false)
  const [isPdfExporting, setIsPdfExporting] = useState(false)
  const [isPdfCreatorOpen, setIsPdfCreatorOpen] = useState(false)
  const [pdfCreatorContent, setPdfCreatorContent] = useState(null)
  const [pdfEditorKey, setPdfEditorKey] = useState('maintenance-pdf-creator')
  const [updatingPluginId, setUpdatingPluginId] = useState(null)
  const [migrationSettings, setMigrationSettings] = useState(null)
  const [message, setMessage] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-site', id],
    queryFn: () => apiMethods.getMaintenanceSite(id),
  })

  const { data: projectsData } = useQuery({
    queryKey: ['maintenance-projects'],
    queryFn: () => apiMethods.getMaintenanceProjects(),
  })

  const requestReportMutation = useMutation({
    mutationFn: () => apiMethods.requestMaintenanceReport(id),
    onSuccess: () => {
      setMessage('Report request sent.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-site', id] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Report request failed.')
    },
  })

  const deleteSiteMutation = useMutation({
    mutationFn: () => apiMethods.deleteMaintenanceSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
      navigate('/maintenance')
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to remove maintenance site.')
    },
  })

  const updatePluginMutation = useMutation({
    mutationFn: (plugin) => apiMethods.updateMaintenancePlugin(id, plugin.id),
    onMutate: (plugin) => {
      setUpdatingPluginId(plugin.id)
      setMessage(`Updating ${plugin.name || plugin.file}...`)
    },
    onSuccess: (response, plugin) => {
      const updatedPlugin = response?.data?.body?.data?.plugin || {}
      setMessage(`${updatedPlugin.name || plugin.name || plugin.file} updated.`)
      queryClient.invalidateQueries({ queryKey: ['maintenance-site', id] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Plugin update failed.')
    },
    onSettled: () => {
      setUpdatingPluginId(null)
    },
  })

  const updateMigrationSettingsMutation = useMutation({
    mutationFn: (payload) => apiMethods.updateMaintenanceSiteMigrationSettings(id, payload),
    onSuccess: () => {
      setMessage('Migration access settings saved.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-site', id] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-site-options'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to save migration settings.')
    },
  })

  const exportVersionChanges = async () => {
    if (!exportFrom) {
      setMessage('Choose export start date.')
      return
    }

    setIsExporting(true)

    try {
      const response = await apiMethods.downloadMaintenanceVersionChanges(id, { from: exportFrom })
      const contentType = response.headers?.['content-type'] || 'text/csv'
      const blob = new Blob([response.data], { type: contentType })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = filenameFromDisposition(response.headers?.['content-disposition']) || `maintenance-version-changes-${exportFrom}-to-current.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setMessage('Version changes export downloaded.')
    } catch (error) {
      setMessage(await exportErrorMessage(error))
    } finally {
      setIsExporting(false)
    }
  }

  const openPdfCreator = async () => {
    setIsPdfExporting(true)

    try {
      const response = await apiMethods.getMaintenanceActivityReport(id, {
        from: exportFrom || undefined,
        include_plugin_versions: 1,
      })
      const report = response.data || {}

      setPdfCreatorContent(buildPdfCreatorDocument(report))
      setPdfEditorKey(`maintenance-pdf-creator-${report.current_report?.id || Date.now()}`)
      setIsPdfCreatorOpen(true)
      setMessage('PDF creator ready. Edit content, then print to PDF.')
    } catch (error) {
      setMessage(error?.response?.data?.message || error?.message || 'Activity PDF export failed.')
    } finally {
      setIsPdfExporting(false)
    }
  }

  const printPdfCreator = () => {
    if (!pdfCreatorContent) {
      setMessage('Prepare the PDF creator before printing.')
      return
    }

    setMessage('Print dialog opened. Choose Save as PDF.')
    window.setTimeout(() => window.print(), 50)
  }

  const updateMigrationComponent = (component, checked) => {
    setMigrationSettings((current) => {
      if (!current) return current

      return {
        ...current,
        migration_capabilities: checked
          ? [...new Set([...current.migration_capabilities, component])]
          : current.migration_capabilities.filter((value) => value !== component),
      }
    })
  }

  const saveMigrationSettings = () => {
    if (!migrationSettings) return

    if (migrationSettings.migration_enabled && !migrationSettings.project_id) {
      setMessage('Choose project before enabling migrations.')
      return
    }

    if (migrationSettings.migration_enabled && !migrationSettings.canonical_host.trim()) {
      setMessage('Canonical host is required before enabling migrations.')
      return
    }

    updateMigrationSettingsMutation.mutate({
      project_id: migrationSettings.project_id ? Number(migrationSettings.project_id) : null,
      canonical_host: migrationSettings.canonical_host || null,
      environment: migrationSettings.environment || null,
      migration_enabled: migrationSettings.migration_enabled,
      migration_capabilities: migrationSettings.migration_capabilities,
    })
  }

  const site = data?.data
  const projects = projectsData?.data || []
  const plugins = useMemo(() => {
    const allPlugins = site?.plugins || []
    const normalizedSearch = search.trim().toLowerCase()

    return allPlugins.filter((plugin) => {
      if (updatesOnly && !plugin.update_available) return false
      if (activeFilter === 'active' && !plugin.active) return false
      if (activeFilter === 'inactive' && plugin.active) return false

      if (!normalizedSearch) return true

      return [plugin.name, plugin.file, plugin.slug]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    })
  }, [site, search, activeFilter, updatesOnly])

  useEffect(() => {
    if (!site) return

    setMigrationSettings({
      project_id: site.project_id ? String(site.project_id) : '',
      canonical_host: site.canonical_host || site.host || '',
      environment: site.environment || '',
      migration_enabled: Boolean(site.migration_enabled),
      migration_capabilities: Array.isArray(site.migration_capabilities) ? site.migration_capabilities : [],
    })
  }, [site])

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        You do not have permission to access Maintenance.
      </div>
    )
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading maintenance site...</div>
  }

  if (!site) {
    return <div className="text-sm text-gray-500">Maintenance site not found.</div>
  }

  return (
    <div className="space-y-6">
      <style>{isPdfCreatorOpen ? pdfCreatorPrintStyles : ''}</style>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            className="mb-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-900"
            onClick={() => navigate('/maintenance')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Maintenance
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{site.name || site.home_url || 'Maintenance Site'}</h1>
          <p className="mt-1 text-sm text-gray-500">{site.home_url || site.site_url || site.site_uuid}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            onClick={() => requestReportMutation.mutate()}
            disabled={requestReportMutation.isPending || deleteSiteMutation.isPending}
          >
            {requestReportMutation.isPending ? 'Requesting...' : 'Request Report'}
          </Button>
          <Button
            type="button"
            variant="destructive"
            aria-label="Remove site"
            title="Remove site"
            onClick={() => {
              if (window.confirm('Remove this maintained site and its report history?')) {
                deleteSiteMutation.mutate()
              }
            }}
            disabled={deleteSiteMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {message ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
          {message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">WordPress</div>
          <div className="mt-2 text-xl font-semibold text-gray-900">{site.wordpress_version || '-'}</div>
          <div className="text-sm text-gray-500">latest {site.wordpress_latest_version || '-'}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Updates</div>
          <div className="mt-2 text-xl font-semibold text-gray-900">{site.update_count}</div>
          <div className="text-sm text-gray-500">core + plugins</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Plugins</div>
          <div className="mt-2 text-xl font-semibold text-gray-900">{site.plugins_count}</div>
          <div className="text-sm text-gray-500">{site.active_plugins_count} active</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Last Report</div>
          <div className="mt-2 text-sm font-semibold text-gray-900">{formatDateTime(site.last_reported_at)}</div>
          <div className="text-sm text-gray-500">{site.last_report_source || '-'}</div>
        </div>
      </div>

      {migrationSettings ? (
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Migration Access</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Enable this site only after canonical host and project membership are correct. Agent checks fail closed on host mismatch or disabled state.
              </p>
            </div>
            <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-medium ${site.migration_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
              {site.migration_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-4">
            <label className="text-sm text-gray-700">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Project</span>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={migrationSettings.project_id}
                onChange={(event) => setMigrationSettings((current) => ({ ...current, project_id: event.target.value }))}
              >
                <option value="">No project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700 lg:col-span-2">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Canonical Host</span>
              <Input
                value={migrationSettings.canonical_host}
                onChange={(event) => setMigrationSettings((current) => ({ ...current, canonical_host: event.target.value }))}
                placeholder="example.com"
              />
            </label>
            <label className="text-sm text-gray-700">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Environment</span>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={migrationSettings.environment}
                onChange={(event) => setMigrationSettings((current) => ({ ...current, environment: event.target.value }))}
              >
                <option value="">Unset</option>
                {ENVIRONMENT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={migrationSettings.migration_enabled}
                  onChange={(event) => setMigrationSettings((current) => ({ ...current, migration_enabled: event.target.checked }))}
                />
                Enable migrations for this site
              </label>
              {MIGRATION_COMPONENTS.map((component) => (
                <label key={component} className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={migrationSettings.migration_capabilities.includes(component)}
                    onChange={(event) => updateMigrationComponent(component, event.target.checked)}
                  />
                  {component}
                </label>
              ))}
            </div>
            <Button
              type="button"
              onClick={saveMigrationSettings}
              disabled={updateMigrationSettingsMutation.isPending}
            >
              {updateMigrationSettingsMutation.isPending ? 'Saving...' : 'Save Migration Access'}
            </Button>
          </div>

          <div className="mt-3 grid gap-3 text-xs text-gray-500 md:grid-cols-3">
            <div>Enabled at: {formatDateTime(site.enabled_at)}</div>
            <div>Disabled at: {formatDateTime(site.disabled_at)}</div>
            <div>Last check: {formatDateTime(site.last_enablement_check_at)}</div>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Exports</h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Export CSV changes or prepare an editable PDF creator document from stored snapshots. Request report first if current state should be refreshed.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="text-sm font-medium text-gray-700">
              From
              <Input
                className="mt-1"
                type="date"
                value={exportFrom}
                onChange={(event) => setExportFrom(event.target.value)}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              onClick={exportVersionChanges}
              disabled={isExporting}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={openPdfCreator}
              disabled={isPdfExporting}
            >
              <Printer className="mr-2 h-4 w-4" />
              {isPdfExporting ? 'Preparing...' : 'PDF Creator'}
            </Button>
          </div>
        </div>
      </div>

      {isPdfCreatorOpen && pdfCreatorContent ? (
        <div className="rounded-lg bg-white p-4 shadow pdf-creator-panel">
          <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-start sm:justify-between pdf-creator-panel-header">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">PDF Creator</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Edit the report text here. Printing uses only this document content, without the old cover page or report footer chrome.
              </p>
            </div>
            <div className="flex gap-2 pdf-creator-actions">
              <Button type="button" variant="secondary" onClick={printPdfCreator}>
                <Printer className="mr-2 h-4 w-4" />
                Print PDF
              </Button>
              <Button
                type="button"
                variant="ghost"
                aria-label="Close PDF creator"
                onClick={() => setIsPdfCreatorOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="pdf-creator-print">
            <div className="pdf-creator-editor-shell">
              <IsolatedLexicalEditor
                initialJSON={pdfCreatorContent}
                onChange={setPdfCreatorContent}
                placeholder="Edit report content before printing..."
                editorKey={pdfEditorKey}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-10"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search plugins"
            />
          </div>
          <select
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
          >
            <option value="">All plugins</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={updatesOnly}
              onChange={(event) => setUpdatesOnly(event.target.checked)}
            />
            Updates only
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Plugin
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Current
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Latest
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {plugins.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={5}>
                    No plugins found.
                  </td>
                </tr>
              ) : (
                plugins.map((plugin) => (
                  <tr key={plugin.id}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{plugin.name || plugin.file}</div>
                      <div className="text-xs text-gray-500">{plugin.file}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{plugin.version || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{plugin.latest_version || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${updateBadge(plugin)}`}>
                          {plugin.update_available ? 'Update available' : 'Current'}
                        </span>
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                          {plugin.active ? 'Active' : 'Inactive'}
                        </span>
                        {plugin.network_active ? (
                          <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                            Network
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {plugin.update_available ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={updatePluginMutation.isPending || deleteSiteMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Update ${plugin.name || plugin.file} on this WordPress site?`)) {
                              updatePluginMutation.mutate(plugin)
                            }
                          }}
                        >
                          <RefreshCw className={`mr-1 h-4 w-4 ${updatingPluginId === plugin.id ? 'animate-spin' : ''}`} />
                          {updatingPluginId === plugin.id ? 'Updating...' : 'Update'}
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Recent Reports</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Reported
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Plugins
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Updates
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {(site.reports || []).length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={4}>
                    No reports yet.
                  </td>
                </tr>
              ) : (
                site.reports.map((report) => (
                  <tr key={report.id}>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(report.reported_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{report.source || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{report.plugin_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{report.update_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  )
}

function changeLabel(change) {
  const labels = {
    version_changed: 'Updated',
    installed: 'Installed',
    removed: 'Removed',
    activation_changed: 'Activation changed',
    network_activation_changed: 'Network activation changed',
    theme_changed: 'Theme changed',
  }

  return labels[change] || change || '-'
}

const pdfCreatorPrintStyles = `
@media print {
  @page { size: A4; margin: 14mm; }
  html, body { background: #fff !important; }
  body * { visibility: hidden !important; }
  .pdf-creator-print, .pdf-creator-print * { visibility: visible !important; }
  .pdf-creator-print { position: absolute; inset: 0; width: 100%; background: #fff; color: #111827; }
  .pdf-creator-panel-header, .pdf-creator-actions { display: none !important; }
  .pdf-creator-editor-shell > div { border: 0 !important; overflow: visible !important; }
  .pdf-creator-editor-shell > div > :first-child { display: none !important; }
  .pdf-creator-editor-shell > div > :nth-child(2) { padding: 0 !important; }
  .pdf-creator-editor-shell [contenteditable] { min-height: 0 !important; outline: 0 !important; font-size: 11pt; line-height: 1.45; color: #111827; }
  .pdf-creator-editor-shell h1 { margin: 0 0 12pt; font-size: 20pt; line-height: 1.2; }
  .pdf-creator-editor-shell h2 { margin: 14pt 0 8pt; font-size: 14pt; line-height: 1.25; }
  .pdf-creator-editor-shell h3 { margin: 10pt 0 6pt; font-size: 12pt; line-height: 1.25; }
  .pdf-creator-editor-shell p { margin: 0 0 6pt; }
}
`
